<template>
  <picture style="display: contents">
    <source
      v-if="avifSrcset"
      type="image/avif"
      :srcset="avifSrcset"
      :sizes="resolvedSizes"
    />
    <source
      v-if="webpSrcset"
      type="image/webp"
      :srcset="webpSrcset"
      :sizes="resolvedSizes"
    />
    <img
      :src="resolvedFallback"
      :alt="alt"
      :class="imageClass"
      :style="imageStyle"
      :width="intrinsic.width"
      :height="intrinsic.height"
      :loading="loading"
      :fetchpriority="fetchpriority"
      :aria-hidden="ariaHidden"
      decoding="async"
      @load="$emit('load', $event)"
      @error="$emit('error', $event)"
      @click="$emit('click', $event)"
    />
  </picture>
</template>

<script setup lang="ts">
import { computed, type PropType } from "vue";
import {
  marketingIntrinsicSize,
  marketingSrcset,
  normalizeMarketingMedia,
  type MarketingMedia,
} from "../../../utils/marketingMedia";

const props = defineProps({
  media: {
    type: Object as PropType<MarketingMedia | Record<string, unknown> | null>,
    default: null,
  },
  fallback: { type: String, default: "" },
  alt: { type: String, default: "" },
  imageClass: { type: [String, Array, Object] as PropType<any>, default: "" },
  imageStyle: { type: [String, Array, Object] as PropType<any>, default: "" },
  sizes: { type: String, default: "100vw" },
  width: { type: Number, default: 1 },
  height: { type: Number, default: 1 },
  loading: { type: String as PropType<"eager" | "lazy">, default: "lazy" },
  fetchpriority: {
    type: String as PropType<"high" | "low" | "auto">,
    default: "auto",
  },
  ariaHidden: {
    type: String as PropType<"true" | "false" | undefined>,
    default: undefined,
  },
});

defineEmits(["load", "error", "click"]);

const normalized = computed(() =>
  normalizeMarketingMedia(props.media, props.fallback)
);
const avifSrcset = computed(() => marketingSrcset(normalized.value, "avif"));
const webpSrcset = computed(() => marketingSrcset(normalized.value, "webp"));
const resolvedFallback = computed(
  () => normalized.value?.fallback || props.fallback
);
const resolvedSizes = computed(
  () => normalized.value?.sizes || props.sizes || "100vw"
);
const intrinsic = computed(() =>
  marketingIntrinsicSize(normalized.value, props.width, props.height)
);
</script>
